import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import styles from "./ReaderFirmwareTabPane.module.scss";

import type { FirmwareDTO, FirmwareType } from "@generated/v2";
import type { TableProps, PaginationConfig } from "antd/lib/table";
import Table, { Column } from "app/components/Table/Table";
import ReaderVersionDeleteButton from "app/screens/ReaderFirmwareScreen/components/ReaderVersionDeleteButton";
import ReaderFirmwareDownloadButton from "app/screens/ReaderFirmwareScreen/components/ReaderFirmwareDownloadButton";

interface IProps {
  firmwareVersions: FirmwareDTO[];
  loading: boolean;
  firmwareType: FirmwareType;
  pagination: PaginationConfig;
  handleTableChange: TableProps<FirmwareDTO>["onChange"];
  refetch: () => void;
}

type FirmwareTableData = Pick<FirmwareDTO, "id" | "version" | "description"> & {
  file: Pick<FirmwareDTO, "url" | "errorMessage">;
};

function ReaderFirmwareTable({
  firmwareVersions,
  loading,
  pagination,
  handleTableChange,
  firmwareType,
  refetch,
}: IProps): React.ReactElement {
  const { t } = useTranslation();

  const dataSource = useMemo(() => {
    return firmwareVersions.map<FirmwareTableData>(e => ({
      id: e.id,
      version: e.version,
      description: e.description,
      file: {
        url: e.url,
        errorMessage: e.errorMessage,
      },
    }));
  }, [firmwareVersions]);

  return (
    <Table
      className={styles.table}
      dataSource={dataSource}
      loading={loading}
      pagination={pagination}
      onChange={handleTableChange}
    >
      <Column
        title={t("readerFirmware.tableCellName")}
        dataIndex="description"
        key="description"
      />
      <Column
        title={t("readerFirmware.tableCellVersionNumber")}
        key="version"
        dataIndex="version"
      />
      <Column
        title={t("desktopVersion.tableCellDownloadLink")}
        key="url"
        dataIndex="file"
        render={(data: FirmwareTableData["file"]) => (
          <ReaderFirmwareDownloadButton
            url={data.url}
            errorMessage={data.errorMessage}
          />
        )}
      />
      <Column
        key="deleteButton"
        dataIndex="id"
        render={id =>
          id ? (
            <ReaderVersionDeleteButton
              id={id}
              type={firmwareType}
              reload={refetch}
            />
          ) : null
        }
      />
    </Table>
  );
}

export default memo(ReaderFirmwareTable);
